/**
 * Favorites Service
 * Handles favorites functionality for all entity types
 * Supports pets, breeders, trainers, vets, and pet shops
 */

import { supabase } from '@/integrations/supabase/client';

export type EntityType = 'pet' | 'breeder' | 'trainer' | 'vet' | 'shop' | 'service' | 'product' | 'lost_found';

const LOCAL_STORAGE_KEY_PREFIX = 'pawsindia_favorites_';

export interface FavoriteItem {
  id: string; // entity_id
  type: EntityType;
  addedAt: string;
}

/**
 * Get local storage key for entity type
 */
function getStorageKey(type: EntityType): string {
  return `${LOCAL_STORAGE_KEY_PREFIX}${type}`;
}

/**
 * Get favorites from local storage (for non-registered users)
 */
export function getLocalFavorites(type: EntityType): string[] {
  try {
    const stored = localStorage.getItem(getStorageKey(type));
    if (!stored) return [];

    const favorites: FavoriteItem[] = JSON.parse(stored);
    return favorites.map(f => f.id);
  } catch (error) {
    console.error(`Error reading local favorites for ${type}:`, error);
    return [];
  }
}

/**
 * Add favorite to local storage
 */
export function addLocalFavorite(type: EntityType, entityId: string): void {
  try {
    const favorites = getLocalFavorites(type);
    if (favorites.includes(entityId)) return;

    const key = getStorageKey(type);
    const stored = localStorage.getItem(key);
    const items: FavoriteItem[] = stored ? JSON.parse(stored) : [];

    items.push({
      id: entityId,
      type,
      addedAt: new Date().toISOString(),
    });

    localStorage.setItem(key, JSON.stringify(items));
  } catch (error) {
    console.error(`Error adding local favorite for ${type}:`, error);
  }
}

/**
 * Remove favorite from local storage
 */
export function removeLocalFavorite(type: EntityType, entityId: string): void {
  try {
    const key = getStorageKey(type);
    const stored = localStorage.getItem(key);
    if (!stored) return;

    const items: FavoriteItem[] = JSON.parse(stored);
    const filtered = items.filter(item => item.id !== entityId);

    localStorage.setItem(key, JSON.stringify(filtered));
  } catch (error) {
    console.error(`Error removing local favorite for ${type}:`, error);
  }
}

/**
 * Clear all local favorites
 */
export function clearLocalFavorites(): void {
  try {
    // Clear for all known types
    const types: EntityType[] = ['pet', 'breeder', 'trainer', 'vet', 'shop', 'service', 'product', 'lost_found'];
    types.forEach(type => {
      localStorage.removeItem(getStorageKey(type));
    });
    // Also clear legacy key
    localStorage.removeItem('pawsindia_favorites');
  } catch (error) {
    console.error('Error clearing local favorites:', error);
  }
}

/**
 * Get favorites from database (for registered users)
 */
export async function getDatabaseFavorites(userId: string, type: EntityType): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .select('entity_id')
      .eq('user_id', userId)
      .eq('entity_type', type)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data as any)?.map((f: any) => f.entity_id) || [];
  } catch (error) {
    console.error(`Error fetching database favorites for ${type}:`, error);
    return [];
  }
}

/**
 * Add favorite to database
 */
export async function addDatabaseFavorite(userId: string, type: EntityType, entityId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('favorites')
      .insert({
        user_id: userId,
        entity_type: type,
        entity_id: entityId,
      } as any);

    if (error) {
      // Ignore duplicate key errors (already favorited)
      if (error.code === '23505') {
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error adding database favorite:', error);
    return false;
  }
}

/**
 * Remove favorite from database
 */
export async function removeDatabaseFavorite(userId: string, type: EntityType, entityId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('user_id', userId)
      .eq('entity_type', type)
      .eq('entity_id', entityId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error removing database favorite:', error);
    return false;
  }
}

/**
 * Sync local favorites to database when user registers/logs in
 */
export async function syncLocalFavoritesToDatabase(userId: string): Promise<void> {
  try {
    const types: EntityType[] = ['pet', 'breeder', 'trainer', 'vet', 'shop', 'service', 'product', 'lost_found'];
    let totalSynced = 0;

    for (const type of types) {
      const localFavorites = getLocalFavorites(type);
      if (localFavorites.length === 0) continue;

      // Get existing database favorites
      const dbFavorites = await getDatabaseFavorites(userId, type);

      // Find favorites that need to be synced
      const favoritesToSync = localFavorites.filter(id => !dbFavorites.includes(id));

      if (favoritesToSync.length > 0) {
        const { error } = await supabase
          .from('favorites')
          .insert(
            favoritesToSync.map(id => ({
              user_id: userId,
              entity_type: type,
              entity_id: id,
            })) as any
          );

        if (!error) {
          totalSynced += favoritesToSync.length;
        }
      }
    }

    // Clear local storage after sync
    clearLocalFavorites();

    if (totalSynced > 0) {
      console.log(`Synced ${totalSynced} favorites to database`);
    }
  } catch (error) {
    console.error('Error syncing local favorites to database:', error);
  }
}

/**
 * Toggle favorite (add or remove)
 */
export async function toggleFavorite(type: EntityType, entityId: string, userId?: string): Promise<{
  isFavorited: boolean;
  success: boolean;
}> {
  try {
    if (userId) {
      // Registered user - use database
      const favorites = await getDatabaseFavorites(userId, type);
      const isFavorited = favorites.includes(entityId);

      if (isFavorited) {
        const success = await removeDatabaseFavorite(userId, type, entityId);
        return { isFavorited: false, success };
      } else {
        const success = await addDatabaseFavorite(userId, type, entityId);
        return { isFavorited: true, success };
      }
    } else {
      // Non-registered user - use local storage
      const favorites = getLocalFavorites(type);
      const isFavorited = favorites.includes(entityId);

      if (isFavorited) {
        removeLocalFavorite(type, entityId);
        return { isFavorited: false, success: true };
      } else {
        addLocalFavorite(type, entityId);
        return { isFavorited: true, success: true };
      }
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
    return { isFavorited: false, success: false };
  }
}

/**
 * Get all favorites (database or local storage)
 */
export async function getAllFavorites(type: EntityType, userId?: string): Promise<string[]> {
  if (userId) {
    return await getDatabaseFavorites(userId, type);
  } else {
    return getLocalFavorites(type);
  }
}

/**
 * Check if an item is favorited
 */
export async function isFavorited(type: EntityType, entityId: string, userId?: string): Promise<boolean> {
  const favorites = await getAllFavorites(type, userId);
  return favorites.includes(entityId);
}

/**
 * Get favorites count
 */
export async function getFavoritesCount(type: EntityType, userId?: string): Promise<number> {
  const favorites = await getAllFavorites(type, userId);
  return favorites.length;
}

// Legacy support function for existing calls (defaults to 'pet')
export async function togglePetFavorite(petId: string, userId?: string) {
  return toggleFavorite('pet', petId, userId);
}
